import {
  type ColorPalette,
  type ColorShades,
  type ColorVariantKey,
  type ColorVariants,
  type VuetifyShadeKey,
  type ShadeName,
  type SingleColor,
  type ThemeColorSet,
  type ThemeGroupExample,
  type VariantExample,
  ThemeName
} from '@/types/theme';

import type { ThemeDefinition } from 'vuetify';

// Luminance threshold for switching text color (typical value)
const LUMINANCE_THRESHOLD = 165;
const DEFAULT_LUMINANCE = 0;

/**
 * Converts a hex color string to its luminance value (0-255).
 * Luminance calculation uses the relative luminance formula for sRGB.
 * @param {string} hex - The hex color string (e.g., '#1a30d5').
 * @returns {number} The luminance value (0-255).
 */
const getLuminance = (hex: string): number => {
  // Ensure the hash is removed
  const hexWithoutHash = hex.replace('#', '');

  if (hexWithoutHash.length !== 6) {
    console.warn(`Invalid hex code length received: ${hex}`);
    return DEFAULT_LUMINANCE;
  }

  // Parse R, G, B components (radix 16)
  const r = parseInt(hexWithoutHash.slice(0, 2), 16);
  const g = parseInt(hexWithoutHash.slice(2, 4), 16);
  const b = parseInt(hexWithoutHash.slice(4, 6), 16);

  // Relative luminance formula (accounts for human perception)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Transforms the structured color palette into the flat object required by Vuetify's theme.colors.
 * @param {object} palette The full color palette object (e.g., colorPalette).
 * @param {string} themeName The key for the theme (e.g., 'light' or 'dark').
 * @returns {object} The flattened colors object for Vuetify theme.
 *
 * @example
 * // If themeName is 'light' and palette has the following values
 * // {
 * //   primary: { shades: { main: 500, darker: 700 }, variants: { 50: '#e9ecfe', 500: '#1a30d5' } },
 * //   secondary: { shades: { main: 500, darker: 700 }, variants: { 50: '#f4f4f6', 500: '#8592a3' } }
 * // }
 * // the output would look like this:
 * // {
 * //   primary: #1a30d5,
 * //   'on-primary': #ffffff,
 * //   'primary-700': #1023a3,
 * //   'on-primary-700': #ffffff,
 * //   'primary-darker': #1023a3,
 * //   'on-primary-darker: #ffffff,
 *
 * //   secondary: #8592a3,
 * //   'on-secondary': #ffffff,
 * //   'secondary-50': #f4f4f6,
 * //   'on-secondary-50': #22303e,
 * //   // ... other colors
 * // }
 */
export const generateVuetifyColors = (
  palette: ColorPalette,
  themeName: ThemeName = ThemeName.light
): ThemeDefinition['colors'] => {
  const themePalette: ThemeColorSet = palette[themeName];

  if (!themePalette) {
    console.error(`Theme '${themeName}' not found in the palette.`);
    return {};
  }

  // ---------------------------------------------------------------------
  // Define and retrieve ON_COLORS from the palette
  // Assumes 'gray' exists in the palette structure and contains '1200' and '0'.
  // Note: the gray's alignment in the style guide is reversed,
  // so we will swap them out for the light / dark theme
  // ---------------------------------------------------------------------
  const { variants: grayVariants } =
    themePalette?.gray || ({} as Partial<ColorVariants>);

  // Apply the `on` contrasting variant based upon the theme.
  // Helper: we want #fafafd for a dark background, and #232333 for a light background
  const ON_LIGHT_BG: string =
    (themeName === ThemeName.light
      ? grayVariants['1000']
      : grayVariants['10']) || '#232333';

  const ON_DARK_BG: string =
    (themeName === ThemeName.light ? grayVariants['0'] : grayVariants['800']) ||
    '#e6e6f1';

  const vuetifyColors: ThemeDefinition['colors'] = {};

  const getColorVariables = (
    hexColor: string,
    colorName: string,
    variableKey: string
  ): { flatVariantName: string; onColor: string } => {
    // Determine contrast color once
    const luminance = getLuminance(hexColor);

    const isDarkBackground = luminance <= LUMINANCE_THRESHOLD;

    // Use short, clear variable name for the contrasting color
    const onColor = isDarkBackground ? ON_DARK_BG : ON_LIGHT_BG;

    // 1. Set the background and on-color for the specific variant (e.g., 'primary-500')
    const flatVariantName = `${colorName}-${variableKey}`;

    return {
      flatVariantName,
      onColor
    };
  };

  // Loop through each top-level color group (e.g., 'primary', 'secondary')
  for (const [colorGroupName, colorGroup] of Object.entries(themePalette) as [
    keyof ThemeColorSet,
    SingleColor
  ][]) {
    // Use destructuring for clearer access.
    // A color group may not have any shades
    const { variants, shades = {} } = colorGroup;

    if (!variants || !shades) continue;

    // Loop through all variants within the group
    for (const [variantKey, hexColor] of Object.entries(variants)) {
      const { flatVariantName, onColor } = getColorVariables(
        hexColor,
        colorGroupName,
        variantKey
      );

      vuetifyColors[flatVariantName] = hexColor;

      // Build the text version with the 'on-' variant coloring
      // eg: a dark background color will generate the 'on-' version with a light text color
      vuetifyColors[`on-${flatVariantName}`] = onColor;
    }

    // Loop through the shades within the group
    for (const [shadeKey, variantValue] of Object.entries(shades) as [
      keyof ColorShades,
      ColorVariantKey
    ][]) {
      // Get the corresponding hex value assigned to the shade's variant
      const hexColor = variants[variantValue] as string;

      // Generate the main color and on-color
      const { flatVariantName, onColor } = getColorVariables(
        hexColor,
        colorGroupName,
        shadeKey
      );

      // ALSO set the root color if this is the designated 'main' variant
      if (shadeKey === 'main') {
        // Sets the root color (e.g., 'primary': '#1a30d5')
        vuetifyColors[colorGroupName] = hexColor;

        // Sets the root on-color (e.g., 'on-primary': '#ffffff')
        vuetifyColors[`on-${colorGroupName}`] = onColor;
        continue;
      }

      vuetifyColors[flatVariantName] = hexColor;

      // Build the text version with the 'on-' variant coloring
      // eg: a dark background color will generate the 'on-' version with a light text color
      vuetifyColors[`on-${flatVariantName}`] = onColor;
    }
  }

  return vuetifyColors;
};

/**
 * Transforms the structured color palette into an array of objects
 * for easy visualization and documentation in the Design Guide
 *
 * @param {object} palette The full color palette object.
 * @param {string} themeName The key for the theme (e.g., 'light').
 * @returns {Array<object>} An array of color groups.
 */
export const generateThemeStructure = (
  palette: ColorPalette,
  themeName: ThemeName = ThemeName.light
): ThemeGroupExample[] => {
  const themePalette: ThemeColorSet = palette[themeName];

  if (!themePalette) {
    console.error(`Theme '${themeName}' not found in the palette.`);
    return [];
  }

  const themeStructure: ThemeGroupExample[] = [];

  for (const [groupName, group] of Object.entries(themePalette) as [
    keyof ThemeColorSet,
    SingleColor
  ][]) {
    // Destructure properties from the group object
    const { variants, shades } = group as SingleColor;

    // Without variants, we have no colors to map
    if (variants) {
      const mapShades: Partial<Record<VuetifyShadeKey, VariantExample>> = {};

      // Loop through each shade in the group
      for (const [shadeKey, variantValue] of Object.entries(shades) as [
        keyof ColorShades,
        ColorVariantKey
      ][]) {
        // For the 'main' shade, we don't want to include a key
        // As the main color is just the root color (e.g., 'primary')
        const semantic: VuetifyShadeKey =
          shadeKey === 'main' ? '' : (shadeKey as Exclude<ShadeName, 'main'>);

        const mappedDetails: VariantExample = {
          // We use the Non-Null Assertion Operator (!) since 'variants' is guaranteed to
          // contain the value corresponding to 'variantValue' in a valid theme setup.
          hexValue: variants[variantValue]!,
          variant: variantValue
        };

        mapShades[semantic] = mappedDetails;
      }

      themeStructure.push({
        // The class name to use with Vuetify's background utility (e.g., 'bg-primary')
        name: groupName, // e.g., 'primary'
        shades: mapShades, // e.g., { '', '#1a30d5', 'soft': '#3a51e2', ... }
        // The full variants object for looping
        variants: variants // e.g., { '50': '#e9ecfe', ... }
      });
    }
  }

  return themeStructure;
};
